/**
 * Universal Shopify Product Compare Script & Modal
 * Manages localStorage deduplication, AJAX fetching, side-by-side comparison modal, and active state sync.
 */
(function (window, document) {
    'use strict';

    var STORAGE_KEY = 'user_compare';
    var MAX_COMPARE_ITEMS = 5;
    var isProcessing = false;

    /* ── 1. Helpers & Storage Management ── */

    var normalizeHandle = function (handle) {
        if (!handle) return '';
        return handle.toString()
            .toLowerCase()
            .trim()
            .replace(/^\/products\//, '')
            .replace(/\/$/, '')
            .replace(/\?.*$/, '')
            .replace(/#.*$/, '');
    };

    var getCompareList = function () {
        try {
            var stored = localStorage.getItem(STORAGE_KEY) || localStorage.getItem('compare');
            if (stored) {
                var parsed = JSON.parse(stored);
                if (Array.isArray(parsed)) {
                    return Array.from(new Set(parsed.map(normalizeHandle).filter(Boolean)));
                }
            }
        } catch (e) {
            console.error('Error reading compare list from localStorage', e);
        }
        return [];
    };

    var saveCompareList = function (list) {
        try {
            var cleanList = Array.from(new Set(list.map(normalizeHandle).filter(Boolean)));
            var jsonStr = JSON.stringify(cleanList);
            localStorage.setItem(STORAGE_KEY, jsonStr);
            localStorage.setItem('compare', jsonStr);
        } catch (e) {
            console.error('Error saving compare list to localStorage', e);
        }
    };

    var formatMoney = function (cents) {
        if (typeof cents === 'string') {
            cents = cents.replace('.', '');
        }
        var formatString = 'Rs. {{amount}}';
        if (typeof Shopify !== 'undefined' && Shopify.money_format) {
            formatString = Shopify.money_format;
        } else if (typeof window.theme !== 'undefined' && window.theme.moneyFormat) {
            formatString = window.theme.moneyFormat;
        }

        var formatted = '';
        if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
            formatted = Shopify.formatMoney(cents, formatString);
        } else {
            var amount = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            formatted = 'Rs. ' + amount;
        }

        if (formatted.indexOf('$') > -1 || formatted.indexOf('₹') > -1) {
            formatted = formatted.replace(/\$/g, 'Rs. ').replace(/₹/g, 'Rs. ');
        }

        return formatted;
    };

    /* ── 2. Modal HTML Injection ── */

    var injectModalHTML = function () {
        if (document.getElementById('ComparePopupModalOverlay')) return;

        var modalHTML = `
        <div id="ComparePopupModalOverlay" class="compare-modal-overlay" style="display:none;" aria-hidden="true">
            <div class="compare-modal-card" role="dialog" aria-modal="true" aria-labelledby="CompareModalTitle">
                <div class="compare-modal-header">
                    <div class="compare-modal-title-area">
                        <h3 id="CompareModalTitle" class="compare-modal-title">Compare Products</h3>
                        <span id="CompareModalBadge" class="compare-modal-badge">0 / ${MAX_COMPARE_ITEMS}</span>
                    </div>
                    <div class="compare-modal-header-actions">
                        <button type="button" id="CompareModalClearBtn" class="compare-modal-btn-clear" title="Clear all compare items">Clear All</button>
                        <button type="button" id="CompareModalCloseBtn" class="compare-modal-btn-close" aria-label="Close modal">&times;</button>
                    </div>
                </div>
                
                <div class="compare-modal-body">
                    <!-- Loading State -->
                    <div id="CompareModalLoader" class="compare-modal-loader" style="display:none;">
                        <div class="compare-spinner"></div>
                        <p>Loading comparison details...</p>
                    </div>

                    <!-- Notification Alert -->
                    <div id="CompareModalAlert" class="compare-modal-alert" style="display:none;"></div>

                    <!-- Empty State -->
                    <div id="CompareModalEmpty" class="compare-modal-empty" style="display:none;">
                        <div class="empty-icon"><i class="pe-7s-shuffle"></i></div>
                        <h4>Your comparison list is empty</h4>
                        <p>Click the compare icon (<i class="pe-7s-shuffle"></i>) on any product to compare details side-by-side.</p>
                        <button type="button" class="compare-modal-btn-continue" onclick="window.CompareModule.closeModal();">Continue Shopping</button>
                    </div>

                    <!-- Comparison Table Container -->
                    <div id="CompareModalContent" class="compare-modal-content"></div>
                </div>
            </div>
        </div>
        `;

        var modalStyles = `
        <style id="CompareModalStyles">
            .compare-modal-overlay {
                position: fixed;
                top: 0;
                left: 0;
                width: 100vw;
                height: 100vh;
                background: rgba(0, 0, 0, 0.65);
                backdrop-filter: blur(5px);
                -webkit-backdrop-filter: blur(5px);
                z-index: 999999;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 15px;
                box-sizing: border-box;
                animation: compareFadeIn 0.25s ease-out;
            }
            @keyframes compareFadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
            }
            .compare-modal-card {
                background: #ffffff;
                width: 100%;
                max-width: 1200px;
                max-height: 90vh;
                border-radius: 12px;
                display: flex;
                flex-direction: column;
                box-shadow: 0 20px 50px rgba(0, 0, 0, 0.35);
                overflow: hidden;
                position: relative;
                animation: compareSlideUp 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
            }
            @keyframes compareSlideUp {
                from { opacity: 0; transform: translateY(30px) scale(0.97); }
                to { opacity: 1; transform: translateY(0) scale(1); }
            }
            .compare-modal-header {
                padding: 18px 24px;
                background: #111111;
                color: #ffffff;
                display: flex;
                align-items: center;
                justify-content: space-between;
                border-bottom: 1px solid #222222;
            }
            .compare-modal-title-area {
                display: flex;
                align-items: center;
                gap: 12px;
            }
            .compare-modal-title {
                margin: 0;
                font-size: 20px;
                font-weight: 700;
                color: #ffffff;
                letter-spacing: 0.5px;
            }
            .compare-modal-badge {
                background: #cc2121;
                color: #ffffff;
                font-size: 12px;
                font-weight: 600;
                padding: 4px 10px;
                border-radius: 12px;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                line-height: 1;
            }
            .compare-modal-header-actions {
                display: flex;
                align-items: center;
                gap: 15px;
            }
            .compare-modal-btn-clear {
                background: transparent;
                border: 1px solid rgba(255, 255, 255, 0.3);
                color: #ffffff;
                font-size: 13px;
                font-weight: 500;
                line-height: 1;
                height: 34px;
                padding: 0 14px;
                border-radius: 4px;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                box-sizing: border-box;
                cursor: pointer;
                transition: all 0.2s ease;
            }
            .compare-modal-btn-clear:hover {
                background: rgba(255, 255, 255, 0.2);
                border-color: #ffffff;
            }
            .compare-modal-btn-close {
                background: rgba(255, 255, 255, 0.15);
                border: 1px solid rgba(255, 255, 255, 0.25);
                color: #ffffff;
                font-size: 22px;
                line-height: 1;
                cursor: pointer;
                padding: 0;
                margin: 0;
                width: 34px;
                height: 34px;
                border-radius: 50%;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                transition: all 0.2s ease;
            }
            .compare-modal-btn-close:hover {
                background: #cc2121;
                border-color: #cc2121;
                color: #ffffff;
                transform: scale(1.08);
            }
            .compare-modal-body {
                padding: 24px;
                overflow-y: auto;
                flex: 1;
                background: #fdfdfd;
            }
            .compare-modal-loader {
                text-align: center;
                padding: 40px 20px;
            }
            .compare-spinner {
                width: 40px;
                height: 40px;
                border: 3px solid #f3f3f3;
                border-top: 3px solid #cc2121;
                border-radius: 50%;
                margin: 0 auto 15px auto;
                animation: compareSpin 0.8s linear infinite;
            }
            @keyframes compareSpin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }
            .compare-modal-alert {
                background: #fff3cd;
                color: #856404;
                border: 1px solid #ffeeba;
                padding: 10px 16px;
                border-radius: 6px;
                margin-bottom: 18px;
                font-size: 14px;
                text-align: center;
            }
            .compare-modal-empty {
                text-align: center;
                padding: 50px 20px;
            }
            .compare-modal-empty .empty-icon {
                font-size: 48px;
                color: #cccccc;
                margin-bottom: 15px;
            }
            .compare-modal-empty h4 {
                font-size: 18px;
                margin-bottom: 8px;
                color: #333333;
            }
            .compare-modal-empty p {
                color: #666666;
                font-size: 14px;
                margin-bottom: 20px;
            }
            .compare-modal-btn-continue {
                background: #111111;
                color: #ffffff;
                border: none;
                height: 44px;
                padding: 0 28px;
                border-radius: 4px;
                font-size: 14px;
                font-weight: 600;
                line-height: 1;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                box-sizing: border-box;
                cursor: pointer;
                transition: background 0.2s ease;
            }
            .compare-modal-btn-continue:hover {
                background: #cc2121;
            }
            .compare-table {
                width: 100%;
                border-collapse: collapse;
                table-layout: fixed;
            }
            .compare-table th, .compare-table td {
                border: 1px solid #eeeeee;
                padding: 14px;
                text-align: center;
                vertical-align: top;
            }
            .compare-table .row-header {
                width: 140px;
                background: #f7f7f7;
                font-weight: 700;
                text-align: left;
                color: #222222;
                font-size: 14px;
            }
            .compare-product-card {
                position: relative;
            }
            .compare-remove-btn {
                position: absolute;
                top: -5px;
                right: -5px;
                background: #e74c3c;
                color: #ffffff;
                border: none;
                border-radius: 50%;
                width: 26px;
                height: 26px;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                font-size: 14px;
                box-shadow: 0 2px 5px rgba(0,0,0,0.2);
                transition: transform 0.2s ease;
            }
            .compare-remove-btn:hover {
                transform: scale(1.15);
                background: #c0392b;
            }
            .compare-img-wrap img {
                max-width: 100%;
                max-height: 180px;
                object-fit: contain;
                border-radius: 6px;
            }
            .compare-product-title {
                font-size: 15px;
                font-weight: 600;
                color: #111111;
                margin: 8px 0;
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                overflow: hidden;
            }
            .compare-price-new {
                font-weight: 700;
                color: #111111;
                font-size: 15px;
            }
            .compare-price-old {
                text-decoration: line-through;
                color: #888888;
                font-size: 13px;
                margin-left: 6px;
            }
            .compare-btn-view {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                width: 100%;
                height: 38px;
                padding: 0 12px;
                background: #111111;
                color: #ffffff !important;
                text-decoration: none !important;
                border-radius: 4px;
                font-weight: 600;
                font-size: 13px;
                line-height: 1;
                box-sizing: border-box;
                text-align: center;
                transition: background 0.2s ease;
            }
            .compare-btn-view:hover {
                background: #cc2121;
            }
            @media (max-width: 767px) {
                .compare-table {
                    display: block;
                    overflow-x: auto;
                    white-space: nowrap;
                    width: auto !important;
                    min-width: 100% !important;
                }
                .compare-table th, .compare-table td {
                    min-width: 150px !important;
                    box-sizing: border-box;
                    white-space: normal !important;
                }
                .compare-table .row-header {
                    position: sticky;
                    left: 0;
                    z-index: 2;
                    min-width: 110px !important;
                    width: 110px !important;
                    background: #f7f7f7 !important;
                    color: #222222 !important;
                }
            }
        </style>
        `;

        document.head.insertAdjacentHTML('beforeend', modalStyles);
        document.body.insertAdjacentHTML('beforeend', modalHTML);

        // Bind events
        document.getElementById('CompareModalCloseBtn').addEventListener('click', closeModal);
        document.getElementById('CompareModalClearBtn').addEventListener('click', clearAll);
        document.getElementById('ComparePopupModalOverlay').addEventListener('click', function (e) {
            if (e.target === this) closeModal();
        });
        document.addEventListener('keydown', function (e) {
            if (e.key === 'Escape') closeModal();
        });
    };

    /* ── 3. Modal Open / Close Logic ── */

    var openModal = function () {
        injectModalHTML();
        var overlay = document.getElementById('ComparePopupModalOverlay');
        if (overlay) {
            overlay.style.display = 'flex';
            overlay.setAttribute('aria-hidden', 'false');
            document.body.style.overflow = 'hidden';
        }
    };

    var closeModal = function () {
        var overlay = document.getElementById('ComparePopupModalOverlay');
        if (overlay) {
            overlay.style.display = 'none';
            overlay.setAttribute('aria-hidden', 'true');
            document.body.style.overflow = '';
        }
    };

    var showAlert = function (msg) {
        var alertEl = document.getElementById('CompareModalAlert');
        if (alertEl) {
            alertEl.textContent = msg;
            alertEl.style.display = 'block';
            setTimeout(function () {
                alertEl.style.display = 'none';
            }, 3500);
        }
    };

    /* ── 4. Render Comparison Table ── */

    var renderComparisonModal = function () {
        var list = getCompareList();
        openModal();

        var badge = document.getElementById('CompareModalBadge');
        if (badge) badge.textContent = list.length + ' / ' + MAX_COMPARE_ITEMS;

        var loader = document.getElementById('CompareModalLoader');
        var empty = document.getElementById('CompareModalEmpty');
        var content = document.getElementById('CompareModalContent');

        if (list.length === 0) {
            if (loader) loader.style.display = 'none';
            if (content) content.style.display = 'none';
            if (empty) empty.style.display = 'block';
            return;
        }

        if (empty) empty.style.display = 'none';
        if (content) content.style.display = 'none';
        if (loader) loader.style.display = 'block';

        // Fetch all product data via AJAX concurrently
        var fetchPromises = list.map(function (handle) {
            return fetch('/products/' + handle + '.js')
                .then(function (res) {
                    if (!res.ok) throw new Error('Product not found: ' + handle);
                    return res.json();
                })
                .catch(function (err) {
                    console.warn('Compare fetch error for handle:', handle, err);
                    return null;
                });
        });

        Promise.all(fetchPromises).then(function (products) {
            var validProducts = products.filter(Boolean);

            if (validProducts.length === 0) {
                if (loader) loader.style.display = 'none';
                if (empty) empty.style.display = 'block';
                return;
            }

            // Build side-by-side table HTML
            var html = '<table class="compare-table"><thead><tr><th class="row-header">Product</th>';

            // Top Row: Product Cards with Remove Button
            validProducts.forEach(function (prod) {
                var img = prod.featured_image ? prod.featured_image : (prod.images && prod.images.length ? prod.images[0] : '');
                html += `
                <td>
                    <div class="compare-product-card">
                        <button type="button" class="compare-remove-btn" title="Remove item" onclick="window.CompareModule.removeProduct('${prod.handle}')">&times;</button>
                        <div class="compare-img-wrap">
                            <a href="${prod.url}">
                                <img src="${img}" alt="${prod.title}">
                            </a>
                        </div>
                        <h4 class="compare-product-title"><a href="${prod.url}">${prod.title}</a></h4>
                    </div>
                </td>`;
            });
            html += '</tr></thead><tbody>';

            // Price Row
            html += '<tr><td class="row-header">Price</td>';
            validProducts.forEach(function (prod) {
                var priceFormatted = formatMoney(prod.price);
                var compareFormatted = prod.compare_at_price > prod.price ? formatMoney(prod.compare_at_price) : '';
                html += `
                <td>
                    <span class="compare-price-new">${priceFormatted}</span>
                    ${compareFormatted ? `<span class="compare-price-old">${compareFormatted}</span>` : ''}
                </td>`;
            });
            html += '</tr>';

            // Vendor Row
            html += '<tr><td class="row-header">Vendor / Brand</td>';
            validProducts.forEach(function (prod) {
                html += `<td>${prod.vendor || 'N/A'}</td>`;
            });
            html += '</tr>';

            // Availability Row
            html += '<tr><td class="row-header">Availability</td>';
            validProducts.forEach(function (prod) {
                var isAvailable = prod.available;
                html += `
                <td>
                    <span style="color: ${isAvailable ? '#27ae60' : '#e74c3c'}; font-weight: 600;">
                        ${isAvailable ? 'In Stock' : 'Out of Stock'}
                    </span>
                </td>`;
            });
            html += '</tr>';

            // Type Row
            html += '<tr><td class="row-header">Category / Type</td>';
            validProducts.forEach(function (prod) {
                html += `<td>${prod.type || 'Standard'}</td>`;
            });
            html += '</tr>';

            // Action Row (View Product Button)
            html += '<tr><td class="row-header">Action</td>';
            validProducts.forEach(function (prod) {
                html += `
                <td>
                    <a href="${prod.url}" class="compare-btn-view">View Details</a>
                </td>`;
            });
            html += '</tr>';

            html += '</tbody></table>';

            if (loader) loader.style.display = 'none';
            if (content) {
                content.innerHTML = html;
                content.style.display = 'block';
            }
        });
    };

    /* ── 5. Compare Actions ── */

    var addOrToggleProduct = function (rawHandle) {
        var handle = normalizeHandle(rawHandle);
        if (!handle) return;

        var list = getCompareList();
        var index = list.indexOf(handle);

        if (index > -1) {
            // Already in compare list -> open modal directly
            renderComparisonModal();
        } else {
            // Check max limit
            if (list.length >= MAX_COMPARE_ITEMS) {
                renderComparisonModal();
                showAlert('Maximum ' + MAX_COMPARE_ITEMS + ' products can be compared at once.');
                return;
            }

            // Add product & save
            list.push(handle);
            saveCompareList(list);
            syncButtonsState();
            renderComparisonModal();
        }
    };

    var removeProduct = function (rawHandle) {
        var handle = normalizeHandle(rawHandle);
        var list = getCompareList();
        var index = list.indexOf(handle);

        if (index > -1) {
            list.splice(index, 1);
            saveCompareList(list);
            syncButtonsState();
            renderComparisonModal();
        }
    };

    var clearAll = function () {
        saveCompareList([]);
        syncButtonsState();
        renderComparisonModal();
    };

    /* ── 6. Sync UI Buttons Across Page ── */

    var syncButtonsState = function () {
        var list = getCompareList();
        var compareBtns = document.querySelectorAll('.btn-compare, .action-btn.compare');
        compareBtns.forEach(function (btn) {
            var handle = btn.getAttribute('data-handle') || btn.getAttribute('data-pid');
            if (handle) {
                var clean = normalizeHandle(handle);
                if (list.indexOf(clean) > -1) {
                    btn.classList.add('is-active', 'active', 'added');
                } else {
                    btn.classList.remove('is-active', 'active', 'added');
                }
            }
        });
    };

    /* ── 7. Global Expose & Inits ── */

    // Global compare() function called by product-grid-item.liquid
    window.compare = function (handle) {
        if (isProcessing) return false;
        isProcessing = true;

        addOrToggleProduct(handle);

        setTimeout(function () {
            isProcessing = false;
        }, 300);

        return false;
    };

    window.CompareModule = {
        add: addOrToggleProduct,
        removeProduct: removeProduct,
        clearAll: clearAll,
        openModal: renderComparisonModal,
        closeModal: closeModal,
        sync: syncButtonsState
    };

    /* ── 8. Standalone Compare Page Support (/pages/compare) ── */

    var loadComparePage = function () {
        var container = document.getElementById('compare-grid-container');
        if (!container) return;

        var loader = document.querySelector('.compare-loader');
        var emptyMsg = document.querySelector('.compare-grid--empty-list');
        var list = getCompareList();

        if (list.length === 0) {
            if (loader) loader.style.display = 'none';
            if (emptyMsg) emptyMsg.style.display = 'block';
            container.innerHTML = '';
            return;
        }

        if (loader) loader.style.display = 'block';
        if (emptyMsg) emptyMsg.style.display = 'none';

        var fetchPromises = list.map(function (handle) {
            return fetch('/products/' + handle + '.js')
                .then(function (res) { return res.ok ? res.json() : null; })
                .catch(function () { return null; });
        });

        Promise.all(fetchPromises).then(function (products) {
            var validProducts = products.filter(Boolean);
            if (loader) loader.style.display = 'none';

            if (validProducts.length === 0) {
                if (emptyMsg) emptyMsg.style.display = 'block';
                container.innerHTML = '';
                return;
            }

            var html = '';
            validProducts.forEach(function (prod) {
                var img = prod.featured_image ? prod.featured_image : (prod.images && prod.images.length ? prod.images[0] : '');
                var priceFormatted = formatMoney(prod.price);
                var compareFormatted = prod.compare_at_price > prod.price ? formatMoney(prod.compare_at_price) : '';

                html += `
                <div class="col-6 col-sm-6 col-md-4 col-lg-3 mb-4 compare-tile-container" data-handle="${prod.handle}">
                    <div class="card h-100 p-3 text-center" style="border: 1px solid #eee; border-radius: 8px; position: relative;">
                        <button type="button" class="btn-remove-tile" onclick="window.CompareModule.removeProduct('${prod.handle}'); window.location.reload();" title="Remove" style="position: absolute; top: 10px; right: 10px; background: #e74c3c; color: #fff; border: none; border-radius: 50%; width: 28px; height: 28px; cursor: pointer;">&times;</button>
                        <a href="${prod.url}">
                            <img src="${img}" alt="${prod.title}" class="img-fluid mb-3" style="max-height: 200px; object-fit: contain;">
                        </a>
                        <h5 class="mb-2"><a href="${prod.url}" style="color: #333; text-decoration: none;">${prod.title}</a></h5>
                        <div class="mb-3">
                            <span style="font-weight: 700; color: #111;">${priceFormatted}</span>
                            ${compareFormatted ? `<span style="text-decoration: line-through; color: #888; margin-left: 6px;">${compareFormatted}</span>` : ''}
                        </div>
                        <a href="${prod.url}" class="btn default-btn btn-primary mt-auto" style="display: flex !important; align-items: center !important; justify-content: center !important; margin-top: auto !important; background: #111 !important; color: #fff !important; border-radius: 4px !important; font-weight: 600 !important; text-decoration: none !important;">View Product</a>
                    </div>
                </div>`;
            });

            container.innerHTML = html;
        });
    };

    // Auto-init on DOM Ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () {
            injectModalHTML();
            syncButtonsState();
            loadComparePage();
        });
    } else {
        injectModalHTML();
        syncButtonsState();
        loadComparePage();
    }

})(window, document);

